import {useEffect} from 'react'
import {shallowEqual} from 'react-redux'

import {getBuild, getBuildType, getProject} from '../../../../selectors'
import type {BuildId} from '../../../../types'
import {deduplicate} from '../../../../utils/array'
import {notNull} from '../../../../utils/guards'
import {
  getBuildTypeSuffix,
  getProjectSuffix,
  getTopics,
  subscribeOnOverallEvents,
} from '../../../../utils/subscriber'
import {
  BUILD_FINISHED,
  BUILD_INTERRUPTED,
  PROBLEM_MUTE_UPDATED,
  PROBLEM_RESPONSIBILITY_CHANGED,
  PROBLEMS_UPDATED,
} from '../../../../utils/subscriptionEvents'

import {useAppSelector} from 'src/hooks/react-redux'

type Props = {
  buildIds: readonly BuildId[]
  handler: () => unknown
}

export function useBuildProblemsSubscriptions({buildIds, handler}: Props) {
  const buildTypeIds = useAppSelector(
    state => deduplicate(buildIds.map(id => getBuild(state, id)?.buildType).filter(notNull)),
    shallowEqual,
  )
  const buildTypeInternalIds = useAppSelector(
    state =>
      deduplicate(buildTypeIds.map(id => getBuildType(state, id)?.internalId).filter(notNull)),
    shallowEqual,
  )
  const projectInternalIds = useAppSelector(
    state =>
      deduplicate(
        buildTypeIds
          .map(id => getProject(state, getBuildType(state, id)?.projectId)?.internalId)
          .filter(notNull),
      ),
    shallowEqual,
  )

  useEffect(() => {
    if (buildTypeInternalIds.length > 0 || projectInternalIds.length > 0) {
      return subscribeOnOverallEvents(
        [
          ...buildTypeInternalIds.flatMap(id =>
            getTopics(getBuildTypeSuffix(id), [BUILD_FINISHED, BUILD_INTERRUPTED]),
          ),
          ...projectInternalIds.flatMap(id =>
            getTopics(getProjectSuffix(id), [
              PROBLEMS_UPDATED,
              PROBLEM_MUTE_UPDATED,
              PROBLEM_RESPONSIBILITY_CHANGED,
            ]),
          ),
        ],
        handler,
      )
    } else {
      handler()
      return undefined
    }
  }, [handler, buildTypeInternalIds, projectInternalIds])
}
